import * as React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';

import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { __t, applyTranslations } from 'utils/language';

import { toggleExplanationBanner } from 'modules/create-new-profiles/reducers/actions';
import {
  CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER,
  CREATE_NEW_PROFILES_MODAL_TEMPLATE_IDENTIFIER
} from 'modules/create-new-profiles/constants/common';

import { State } from 'types';

import './create-new-profiles-form-explanation-banner.scss';

applyTranslations(require('./create-new-profiles-form-explanation-banner.i18n.json'));

interface StateProps {
  providedFor:
    | typeof CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER
    | typeof CREATE_NEW_PROFILES_MODAL_TEMPLATE_IDENTIFIER,
  isCollapsed: boolean
}

interface DispatchProps {
  dispatchToggleExplanationBanner: () => void
}

type Props = StateProps & DispatchProps;

const CreateNewProfilesFormExplanationBanner = ({
  providedFor,
  isCollapsed,
  dispatchToggleExplanationBanner
}: Props): JSX.Element => {
  const bannerClassNames = classnames({
    'create-new-profiles-form-explanation-banner': true,
    'create-new-profiles-form-explanation-banner--clickable':
      providedFor === CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER,
    'create-new-profiles-form-explanation-banner--collapsed': isCollapsed
  });
  const bannerHeaderClassNames = classnames({
    'create-new-profiles-form-explanation-banner__header': true,
    'create-new-profiles-form-explanation-banner__header--clickable':
      providedFor === CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER
  });

  return (
    <div className={bannerClassNames}>
      <div
        className={bannerHeaderClassNames}
        onClick={() => {
          if (providedFor === CREATE_NEW_PROFILES_MODAL_TEMPLATE_IDENTIFIER) {
            return;
          }

          dispatchToggleExplanationBanner();
        }}
      >
        <div className="create-new-profiles-form-explanation-banner__header-info-icon">
          <SVGDynamicIcon
            name="info-inside-circle-custom-bg"
            width={24}
            height={24}
            customColor="blue"
          />
        </div>
        <span className="create-new-profiles-form-explanation-banner__header-text">
          {__t('create_new_profiles_form_explanation_banner.header')}
        </span>
        {providedFor === CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER ? (
          <div className="create-new-profiles-form-explanation-banner__header-chevron-icon">
            <SVGDynamicIcon
              name={
                isCollapsed
                  ? 'chevron-down-custom-fg'
                  : 'chevron-up-custom-fg'
              }
              width={15}
              height={8}
              customColor="gray"
            />
          </div>
        ) : null}
      </div>
      <div className="create-new-profiles-form-explanation-banner__content">
        <ul>
          <li>{__t('create_new_profiles_form_explanation_banner.list1.line1')}</li>
          <li>{__t('create_new_profiles_form_explanation_banner.list1.line2')}</li>
          <li>{__t('create_new_profiles_form_explanation_banner.list1.line3')}</li>
        </ul>
        <span
          dangerouslySetInnerHTML={{
            __html: __t('create_new_profiles_form_explanation_banner.list1_questions')
          }}
        />
        <div className="create-new-profiles-form-explanation-banner__subheader">
          {
            providedFor === CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER
              ? __t('create_new_profiles_form_explanation_banner.manual_list2_subheader')
              : __t('create_new_profiles_form_explanation_banner.template_list2_subheader')
          }
        </div>
        {providedFor === CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER ? (
          <ul>
            <li>{__t('create_new_profiles_form_explanation_banner.manual_list2.line1')}</li>
            <li>{__t('create_new_profiles_form_explanation_banner.manual_list2.line2')}</li>
          </ul>
        ) : (
          <ul>
            <li>{__t('create_new_profiles_form_explanation_banner.template_list2.line1')}</li>
            <li>{__t('create_new_profiles_form_explanation_banner.template_list2.line2')}</li>
            <li>{__t('create_new_profiles_form_explanation_banner.template_list2.line3')}</li>
            <li>{__t('create_new_profiles_form_explanation_banner.template_list2.line4')}</li>
          </ul>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = ({ createNewProfiles }: State): StateProps => {
  const { createEntitiesModal } = createNewProfiles;

  if (createEntitiesModal.type === CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER) {
    return {
      providedFor: CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER,
      isCollapsed: createEntitiesModal.isExplanationBannerCollapsed
    };
  }

  return {
    providedFor: CREATE_NEW_PROFILES_MODAL_TEMPLATE_IDENTIFIER,
    isCollapsed: false
  };
};

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchToggleExplanationBanner: () => dispatch(toggleExplanationBanner())
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateNewProfilesFormExplanationBanner);
